import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "../ui/Input";
import { Label } from "../ui/Label";
import { Button } from "../ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/Card";
import { Link } from "react-router-dom";
import { EyeOpenIcon, EyeClosedIcon } from "@radix-ui/react-icons";

const newPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[0-9]/, "Password must contain at least one number"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type NewPasswordSchemaType = z.infer<typeof newPasswordSchema>;

interface CreateNewPasswordProps {
  onSubmit: (data: NewPasswordSchemaType) => Promise<void>;
  isLoading?: boolean;
}

export const CreateNewPassword: React.FC<CreateNewPasswordProps> = ({
  onSubmit,
  isLoading = false,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<NewPasswordSchemaType>({
    resolver: zodResolver(newPasswordSchema),
    mode: "onChange", // Validate on change to check passwords match in real-time
  });

  const password = watch("password");
  const confirmPassword = watch("confirmPassword");
  const passwordsMatch = password && confirmPassword && password === confirmPassword;

  return (
    <Card className="w-full max-w-md bg-white shadow-none border border-border-gray rounded-none">
      <CardHeader className="space-y-3 pb-6 pt-8">
        <CardTitle className="text-center text-xl font-bold uppercase tracking-wide">
          CREATE A NEW PASSWORD
        </CardTitle>
        <CardDescription className="text-center text-sm text-gray-600">
          Choose a New Password
          <br />
          for your Account
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 px-8 pb-8">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-0">
          <div className="relative pb-5">
            <Label htmlFor="password" className="sr-only">
              Password
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                {...register("password")}
                error={!!errors.password}
                className="w-full h-12 bg-disabled-bg border-border-gray rounded-none placeholder:text-gray-500 pr-10"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                disabled={isLoading}
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeClosedIcon className="h-5 w-5" />
                ) : (
                  <EyeOpenIcon className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="text-xs text-red-500 absolute left-0 top-[calc(100%-1.25rem)]">
                {errors.password.message}
              </p>
            )}
          </div>

          <div className="relative pb-5">
            <Label htmlFor="confirmPassword" className="sr-only">
              Confirm Password
            </Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm New Password"
                {...register("confirmPassword")}
                error={!!errors.confirmPassword}
                className="w-full h-12 bg-disabled-bg border-border-gray rounded-none placeholder:text-gray-500 pr-10"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                disabled={isLoading}
                tabIndex={-1}
              >
                {showConfirmPassword ? (
                  <EyeClosedIcon className="h-5 w-5" />
                ) : (
                  <EyeOpenIcon className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-xs text-red-500 absolute left-0 top-[calc(100%-1.25rem)]">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full h-12 bg-primary-blue hover:bg-primary-blue/90 text-white font-normal rounded-none mt-6"
            disabled={isLoading || !isValid || !passwordsMatch}
          >
            {isLoading ? "Updating..." : "Continue"}
          </Button>
        </form>

        <p className="text-sm text-gray-600 text-center pt-4">
          Having trouble?{" "}
          <Link to="/contact" className="text-link-blue hover:underline">
            Contact Support
          </Link>
        </p>
      </CardContent>
    </Card>
  );
};
